import type { MessengerChatRate } from '@widgets/messenger/types/chat/chat'
import type { MessengerApiAttachment } from '@widgets/messenger/types/api/common'
import type { MessengerWebSocketEventMap } from '@widgets/messenger/types/api/websocket'
import type { Nullable } from '@/types'
import type { ServerEventsUnionFromMap } from '@/services/transport/types'

export type MessengerGetChatResponse = {
  token: string
  assignee: Nullable<{
    firstName: string
    lastName: string
    avatarHref: string
  }>
}

export type MessengerPreChatResponse = MessengerGetChatResponse

export type MessengerSendMessageRequest = {
  message: string
  fileIds: string[]
  trackId?: string
  replyToMessageId?: string
}

export type MessengerSendMessageResponse = {
  id: number
}

export type MessengerChatSurveyRequest = {
  rate?: MessengerChatRate
  comment?: string
}

export type MessengerRestApiPaginatedResponse<T> = {
  items: T[]
  pagination: {
    perPage: number
    page: number
    totalCount: number
    pageCount: number
    overLimit: boolean
    nextId: Nullable<number>
    prevId: Nullable<number>
    type: 'offset'
  }
}

export type MessengerGetMessagesResponse = MessengerRestApiPaginatedResponse<MessengerRestApiMessage>

export type MessengerUploadAttachmentRequest = {
  file: File
}

export type MessengerUploadAttachmentResponse = MessengerApiAttachment

export type MessengerEventHistoryResponse = {
  items: ServerEventsUnionFromMap<MessengerWebSocketEventMap>[]
}

export type MessengerSendSmsRequest = {
  name: string
  number: string
  message: string
}

export type MessengerSendEmailRequest = {
  name: string
  email: string
  message: string
  fileIds: string[]
}

// Messages

type MessageType = 'text' | 'initial' | 'log'
type MessageChannel = 'incoming' | 'outgoing' | 'all'
type MessageStatus = 'delivered' // TODO add another possible statuses
type MessageCreatedByType = 'contact' | 'user'
type MessageCreatedBy<T extends MessageCreatedByType = MessageCreatedByType> = {
  id: number
  type: T
}
type MessageUser = {
  firstName: string
  lastName: string
}

interface MessengerRestApiMessageAbstract<T extends MessageType> {
  id: number
  type: T
  createdAt: string // ISO, example: 2024-09-12T08:51:15+0000
  updatedAt: string // ISO, example: 2024-09-12T08:51:15+0000
  channel: MessageChannel
  payload: Record<string, unknown>
  status: MessageStatus
  files: MessengerApiAttachment[]
}

export interface MessengerRestApiSentTextMessage extends MessengerRestApiMessageAbstract<'text'> {
  payload: {
    createdBy: MessageCreatedBy<'contact'>
    direction: 'in'
    content: string
    isEdited: boolean
    replyToMessageId: Nullable<number>
    type: 'text'
  }
}

export interface MessengerRestApiIncomingTextMessage extends MessengerRestApiMessageAbstract<'text'> {
  payload: {
    createdBy: MessageCreatedBy<'user'>
    direction: 'out'
    content: string
    isEdited: boolean
    replyToMessageId: Nullable<number>
    type: 'text'
  }
  user: MessageUser
}

export interface MessengerRestApiInitialMessage extends MessengerRestApiMessageAbstract<'initial'> {
  payload: {
    email: string
    text: string
    name: string
    type: 'initial'
  }
}

export interface MessengerRestApiUserJoinedLogMessage extends MessengerRestApiMessageAbstract<'log'> {
  payload: {
    payload: {
      type: 'userJoined'
    }
    createdBy: MessageCreatedBy<'user'>
  }
  user: MessageUser
}

export interface MessengerRestApiUserLeftLogMessage extends MessengerRestApiMessageAbstract<'log'> {
  payload: {
    payload: {
      all: boolean
      type: 'userLeft'
    }
    createdBy: MessageCreatedBy<'user'>
  }
  user: MessageUser
}

export interface MessengerRestApiChatRatedLogMessage extends MessengerRestApiMessageAbstract<'log'> {
  payload: {
    payload: {
      type: 'chatRated'
      payload: {
        rate: MessengerChatRate
      }
    }
    createdBy: MessageCreatedBy<'contact'>
  }
}

export interface MessengerRestApiCommentLeftLogMessage extends MessengerRestApiMessageAbstract<'log'> {
  payload: {
    payload: {
      type: 'commentLeft'
      payload: {
        content: string
      }
    }
    createdBy: MessageCreatedBy<'contact'>
  }
}

export type MessengerRestApiLogMessage =
  | MessengerRestApiUserJoinedLogMessage
  | MessengerRestApiUserLeftLogMessage
  | MessengerRestApiChatRatedLogMessage
  | MessengerRestApiCommentLeftLogMessage

export type MessengerRestApiMessage =
  | MessengerRestApiSentTextMessage
  | MessengerRestApiIncomingTextMessage
  | MessengerRestApiInitialMessage
  | MessengerRestApiLogMessage

export const isRestApiSentTextMessage = (
  message: MessengerRestApiMessage,
): message is MessengerRestApiSentTextMessage => {
  return message.type === 'text' && message.payload.direction === 'in'
}

export const isRestApiIncomingTextMessage = (
  message: MessengerRestApiMessage,
): message is MessengerRestApiIncomingTextMessage => {
  return message.type === 'text' && message.payload.direction === 'out'
}

export const isRestApiInitialMessage = (
  message: MessengerRestApiMessage,
): message is MessengerRestApiInitialMessage => {
  return message.type === 'initial'
}

export const isRestApiLogMessage = (message: MessengerRestApiMessage): message is MessengerRestApiLogMessage => {
  return message.type === 'log'
}

export const isRestApiUserJoinedLogMessage = (
  message: MessengerRestApiMessage,
): message is MessengerRestApiUserJoinedLogMessage => {
  return isRestApiLogMessage(message) && message.payload.payload.type === 'userJoined'
}

export const isRestApiUserLeftLogMessage = (
  message: MessengerRestApiMessage,
): message is MessengerRestApiUserLeftLogMessage => {
  return isRestApiLogMessage(message) && message.payload.payload.type === 'userLeft'
}

export const isRestApiChatRatedLogMessage = (
  message: MessengerRestApiMessage,
): message is MessengerRestApiChatRatedLogMessage => {
  return isRestApiLogMessage(message) && message.payload.payload.type === 'chatRated'
}

export const isRestApiCommentLeftLogMessage = (
  message: MessengerRestApiMessage,
): message is MessengerRestApiCommentLeftLogMessage => {
  return isRestApiLogMessage(message) && message.payload.payload.type === 'commentLeft'
}
